import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import type { PursuitSummary } from '@/types/chat-v2/tools'
import type { Color } from '@/types/Color'

/**
 * Zustand store for PursuitSummary and color data.
 * Populated by pursuit_search tool, consumed by pursuit_explore tool.
 * This allows instant access to rich pursuit data (stage, due date, value, pwin)
 * and stage/tag colors without making additional API calls.
 */

interface PursuitCacheState {
  // Data
  pursuitSummaries: Record<string, PursuitSummary>
  stageColors: Record<string, Color>
  tagColors: Record<string, Color>

  // Actions
  addPursuitSummaries: (summaries: PursuitSummary[]) => void
  addStageColors: (colors: Record<string, Color> | undefined) => void
  addTagColors: (colors: Record<string, Color> | undefined) => void
  clear: () => void
}

export const usePursuitCacheStore = create<PursuitCacheState>()(
  devtools(
    immer((set) => ({
      // Initial state
      pursuitSummaries: {},
      stageColors: {},
      tagColors: {},

      // Actions
      addPursuitSummaries: (summaries) => {
        if (summaries.length === 0) return
        set((state) => {
          for (const summary of summaries) {
            state.pursuitSummaries[summary.pursuit_id] = summary
          }
        })
      },

      addStageColors: (colors) => {
        if (!colors) return
        const entries = Object.entries(colors)
        if (entries.length === 0) return
        set((state) => {
          for (const [name, color] of entries) {
            state.stageColors[name] = color
          }
        })
      },

      addTagColors: (colors) => {
        if (!colors) return
        const entries = Object.entries(colors)
        if (entries.length === 0) return
        set((state) => {
          for (const [name, color] of entries) {
            state.tagColors[name] = color
          }
        })
      },

      clear: () =>
        set((state) => {
          state.pursuitSummaries = {}
          state.stageColors = {}
          state.tagColors = {}
        }),
    })),
    { name: 'pursuit-cache-store' },
  ),
)

// ============================================================================
// Convenience hooks with memoized selectors
// ============================================================================

/**
 * Hook to get a pursuit summary from the cache.
 * Only re-renders when the specific pursuit changes.
 */
export function useCachedPursuitSummary(
  pursuitId: string | undefined,
): PursuitSummary | undefined {
  return usePursuitCacheStore((state) =>
    pursuitId ? state.pursuitSummaries[pursuitId] : undefined,
  )
}

/**
 * Hook to get a stage color from the cache.
 * Only re-renders when the specific stage color changes.
 */
export function useCachedStageColor(
  stageName: string | null | undefined,
): Color | undefined {
  return usePursuitCacheStore((state) =>
    stageName ? state.stageColors[stageName] : undefined,
  )
}

// ============================================================================
// Non-hook exports for use outside React components (e.g., in useEffect)
// ============================================================================

/**
 * Add or update pursuit summaries in the cache.
 * Called by pursuit_search tool when it receives results.
 */
export function addPursuitSummariesToCache(summaries: PursuitSummary[]): void {
  usePursuitCacheStore.getState().addPursuitSummaries(summaries)
}

/**
 * Add or update stage colors in the cache.
 * Called by pursuit_search tool when it receives metadata.
 */
export function addStageColorsToCache(
  colors: Record<string, Color> | undefined,
): void {
  usePursuitCacheStore.getState().addStageColors(colors)
}

/**
 * Add or update tag colors in the cache.
 * Called by pursuit_search or get_metadata tool when it receives metadata.
 */
export function addTagColorsToCache(
  colors: Record<string, Color> | undefined,
): void {
  usePursuitCacheStore.getState().addTagColors(colors)
}

/**
 * Clear all caches. Call this when switching organizations or logging out
 * to prevent memory leaks and stale data.
 */
export function clearPursuitCaches(): void {
  usePursuitCacheStore.getState().clear()
}
